import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  SelectChangeEvent,
  Typography,
  Stack,
  Autocomplete,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import businessService from '../../../services/businessService';
import formService from '../../../services/formService';
import userService from '../../../services/userService';

interface ReportFiltersProps {
  onGenerate: (filters: any) => void;
  loading: boolean;
  userRole: string;
}

interface Business {
  id: number;
  name: string;
  industry?: string;
  address?: string;
}

interface Form {
  id: number;
  name: string;
}

interface User {
  id: number;
  name: string;
}

const ReportFilters: React.FC<ReportFiltersProps> = ({ onGenerate, loading, userRole }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [negocios, setNegocios] = useState<Business[]>([]);
  const [formularios, setFormularios] = useState<Form[]>([]);
  const [auditores, setAuditores] = useState<User[]>([]);

  const [businessSelected, setBusinessSelected] = useState<Business | null>(null);
  const [negocioId, setNegocioId] = useState<number | ''>('');
  const [presetPeriodo, setPresetPeriodo] = useState<string>('mes_actual');
  const [fechaDesde, setFechaDesde] = useState<string>('');
  const [fechaHasta, setFechaHasta] = useState<string>('');
  const [formularioId, setFormularioId] = useState<number | ''>('');
  const [auditorId, setAuditorId] = useState<number | ''>('');
  const [estados, setEstados] = useState<string[]>(['completed']);

  // Cargar datos iniciales
  useEffect(() => {
    loadNegocios();
    loadFormularios();
    loadAuditores();
    calcularFechas('mes_actual');
  }, []);

  const loadNegocios = async () => {
    try {
      const result = await businessService.list({ limit: 1000 }); // Cargar todos los negocios
      const data = result.data || [];
      setNegocios(data);
      
      // Si es manager y solo tiene un negocio asignado, seleccionarlo automáticamente
      if (userRole === 'manager' && data.length === 1) {
        setNegocioId(data[0].id);
        setBusinessSelected(data[0]);
      }
    } catch (error) {
      console.error('Error al cargar negocios:', error);
    }
  };

  const loadFormularios = async () => {
    try {
      const result = await formService.list();
      const data = result.data || [];
      setFormularios(data);
    } catch (error) {
      console.error('Error al cargar formularios:', error);
    }
  };

  const loadAuditores = async () => {
    try {
      const result = await userService.list({ role: 'auditor' });
      const data = result.data || [];
      setAuditores(data);
    } catch (error) {
      console.error('Error al cargar auditores:', error);
    }
  };

  const calcularFechas = (preset: string) => {
    const hoy = new Date();
    let desde = new Date();
    let hasta = new Date();

    switch (preset) {
      case 'mes_actual':
        desde = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
        hasta = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
        break;
      case 'mes_anterior':
        desde = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1);
        hasta = new Date(hoy.getFullYear(), hoy.getMonth(), 0);
        break;
      case 'ultimos_3_meses':
        desde = new Date(hoy.getFullYear(), hoy.getMonth() - 3, hoy.getDate());
        hasta = hoy;
        break;
      case 'personalizado':
        return; // No modificar las fechas
      default:
        break;
    }

    // Usar la fecha local (no UTC) para evitar corrimiento de zona horaria
    const toLocalStr = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };

    setFechaDesde(toLocalStr(desde));
    setFechaHasta(toLocalStr(hasta));
  };

  const handlePresetChange = (event: SelectChangeEvent) => {
    const preset = event.target.value;
    setPresetPeriodo(preset);
    calcularFechas(preset);
  };

  const handleEstadosChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setEstados(typeof value === 'string' ? value.split(',') : value);
  };

  const handleGenerate = () => {
    if (!negocioId || !fechaDesde || !fechaHasta) {
      alert('Debe seleccionar un negocio y un periodo');
      return;
    }

    // Validar que fecha_desde sea anterior a fecha_hasta
    if (new Date(fechaDesde) > new Date(fechaHasta)) {
      alert('La fecha desde debe ser anterior a la fecha hasta');
      return;
    }

    // Validar que el periodo no exceda 1 año
    const duracionMs = new Date(fechaHasta).getTime() - new Date(fechaDesde).getTime();
    const duracionDias = duracionMs / (1000 * 60 * 60 * 24);
    if (duracionDias > 365) {
      alert('El periodo no puede exceder 1 año');
      return;
    }

    const filters = {
      business_id: Number(negocioId),
      fecha_desde: fechaDesde,
      fecha_hasta: fechaHasta,
      form_id: formularioId ? Number(formularioId) : null,
      auditor_id: auditorId ? Number(auditorId) : null,
      estados
    };

    onGenerate(filters);
  };

  const handleClear = () => {
    setNegocioId('');
    setBusinessSelected(null);
    setPresetPeriodo('mes_actual');
    setFormularioId('');
    setAuditorId('');
    setEstados(['completed']);
    calcularFechas('mes_actual');
  };

  return (
    <Paper 
      elevation={2} 
      sx={{ 
        p: isMobile ? 1.5 : 2, 
        mb: 3,
        width: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden'
      }}
    >
      <Typography 
        variant={isMobile ? 'body2' : 'body1'} 
        gutterBottom 
        sx={{ mb: 1.5, fontWeight: 700 }}
      >
        Filtros del Reporte
      </Typography>

      <Grid container spacing={isMobile ? 1 : 1.5}>
        {/* Fila 1: Negocio | Periodo | Desde | Hasta */}
        <Grid item xs={12} sm={6} md={4}>
          <Autocomplete
            value={businessSelected}
            onChange={(_event, newValue) => {
              setBusinessSelected(newValue);
              setNegocioId(newValue ? newValue.id : '');
            }}
            options={negocios}
            getOptionLabel={(option) => option.name}
            disabled={loading}
            size="small"
            renderInput={(params) => (
              <TextField
                {...params}
                id="negocio-field"
                name="negocio"
                label="Negocio *"
                placeholder="Buscar..."
                size="small"
              />
            )}
            renderOption={(props, option) => {
              const { key, ...otherProps } = props;
              return (
                <Box component="li" key={key} {...otherProps}>
                  <Box>
                    <Typography variant="body2">{option.name}</Typography>
                    {option.industry && (
                      <Typography variant="caption" color="text.secondary">
                        {option.industry}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            }}
            filterOptions={(options, { inputValue }) => {
              const searchLower = inputValue.toLowerCase();
              return options.filter(
                (option) =>
                  option.name?.toLowerCase().includes(searchLower) ||
                  (option.industry && option.industry.toLowerCase().includes(searchLower))
              );
            }}
            noOptionsText="No se encontraron negocios"
            isOptionEqualToValue={(option, value) => option.id === value.id}
            ListboxProps={{ style: { maxHeight: '280px' } }}
          />
        </Grid>

        <Grid item xs={6} sm={3} md={2}>
          <FormControl fullWidth size="small" required>
            <InputLabel id="periodo-label">Periodo</InputLabel>
            <Select
              labelId="periodo-label"
              value={presetPeriodo}
              label="Periodo"
              onChange={handlePresetChange}
              disabled={loading}
            >
              <MenuItem value="mes_actual">Mes actual</MenuItem>
              <MenuItem value="mes_anterior">Mes anterior</MenuItem>
              <MenuItem value="ultimos_3_meses">Últ. 3 meses</MenuItem>
              <MenuItem value="personalizado">Personalizado</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={6} sm={3} md={3}>
          <TextField
            fullWidth
            size="small"
            required
            type="date"
            label="Desde"
            value={fechaDesde}
            onChange={(e) => setFechaDesde(e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={presetPeriodo !== 'personalizado' || loading}
          />
        </Grid>

        <Grid item xs={6} sm={3} md={3}>
          <TextField
            fullWidth
            size="small"
            required
            type="date"
            label="Hasta"
            value={fechaHasta}
            onChange={(e) => setFechaHasta(e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={presetPeriodo !== 'personalizado' || loading}
          />
        </Grid>

        {/* Fila 2: Formulario | Auditor | Estados | Botones */}
        <Grid item xs={6} sm={3} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="formulario-label">Formulario</InputLabel>
            <Select
              labelId="formulario-label"
              value={formularioId}
              label="Formulario"
              onChange={(e) => setFormularioId(e.target.value as number)}
              disabled={loading}
            >
              <MenuItem value="">Todos</MenuItem>
              {formularios.map((formulario) => (
                <MenuItem key={formulario.id} value={formulario.id}>
                  {formulario.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={6} sm={3} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="auditor-label">Auditor</InputLabel>
            <Select
              labelId="auditor-label"
              value={auditorId}
              label="Auditor"
              onChange={(e) => setAuditorId(e.target.value as number)}
              disabled={loading}
            >
              <MenuItem value="">Todos</MenuItem>
              {auditores.map((auditor) => (
                <MenuItem key={auditor.id} value={auditor.id}>
                  {auditor.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="estados-label">Estados</InputLabel>
            <Select
              labelId="estados-label"
              multiple
              value={estados}
              onChange={handleEstadosChange}
              input={<OutlinedInput label="Estados" size="small" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
              disabled={loading}
            >
              <MenuItem value="completed">Completada</MenuItem>
              <MenuItem value="synchronized">Sincronizada</MenuItem>
              <MenuItem value="draft">Borrador</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Stack direction="row" spacing={1} justifyContent={isMobile ? 'flex-end' : 'flex-end'} alignItems="center" height="100%">
            <Button
              variant="outlined"
              size="small"
              startIcon={<ClearIcon />}
              onClick={handleClear}
              disabled={loading}
            >
              Limpiar
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<SearchIcon />}
              onClick={handleGenerate}
              disabled={loading || !negocioId || !fechaDesde || !fechaHasta}
            >
              Generar
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default ReportFilters;
